// @ts-check
import { useContext, useRef } from 'react';
import { ModalStackDepthContext } from '../../state/ModalStackDepthContext.js';
import { ModalCloseContext, resolveModalSize } from './modalSizes.js';

/**
 * The shared modal shell. Every modal in the app renders through this component
 * — do not fork it (see the UI & Branding rules in CLAUDE.md).
 *
 * @param {Object} props
 * @param {import('react').ReactNode} props.title
 * @param {() => void} props.onClose
 *   Called after the unsaved-changes confirmation passes (or is not needed).
 * @param {() => void} [props.onBack]
 *   When supplied, renders a back arrow left of the title.
 * @param {import('react').ReactNode} [props.children]
 * @param {'sm' | 'md' | 'lg'} [props.size]
 *   Named width: sm 480, md 580 (default), lg 820.
 * @param {boolean} [props.wide]
 *   Legacy alias for `size="lg"`. Ignored when `size` is set.
 * @param {number | string} [props.maxWidth]
 *   Frozen debt: an explicit max width overriding the named sizes. Existing
 *   call sites keep working; new ones use `size`.
 * @param {number | string} [props.fixedHeight]
 *   Locks the modal box to this height (capped at the default 90vh) instead of
 *   letting it grow/shrink to fit whichever tab is active. Use for step/tab-based
 *   modals where switching tabs would otherwise resize and reposition the box.
 * @param {boolean} [props.dirty]
 *   Explicit unsaved-changes flag. When omitted, the modal falls back to
 *   auto-detecting any `input` event fired inside its body.
 * @param {string} [props.dirtyMessage]
 *   The confirm text shown when closing while dirty. Defaults to the generic
 *   unsaved-changes prompt; a modal whose close is not a plain discard (e.g.
 *   onboarding, where closing skips it for good) says what closing does.
 * @param {boolean} [props.closeOnBackdrop]
 *   Default true. False makes a click outside the box do nothing, for a modal
 *   whose close is permanent and must only ever be an explicit ✕.
 * @param {boolean} [props.closable]
 *   Default true. False hides ✕ and makes the backdrop inert, for a forced
 *   choice whose every way out is one of the body's own buttons (a save
 *   conflict, where dismissing would let the next save overwrite silently).
 * @param {import('react').ReactNode} [props.headerActions]
 *   Rendered in the header, left of the close button.
 * @param {import('react').ReactNode} [props.footer]
 *   Rendered in a dedicated footer strip below the scrollable body — e.g. a
 *   Cancel/Save action row. Unlike pinning that row inside `children` with
 *   `position: sticky`, this footer sits outside the scroll container, so it
 *   never overlaps the last row of scrollable content (a sticky bar inside
 *   modal-body covers whatever content lands under it once the user scrolls
 *   past the end, and a plain mouse-wheel scroll — as opposed to a
 *   scroll-into-view — has no way to reserve space for it).
 */
function Modal({
  title,
  onClose,
  onBack,
  children,
  size,
  wide,
  maxWidth,
  fixedHeight,
  dirty,
  dirtyMessage,
  closeOnBackdrop = true,
  closable = true,
  headerActions,
  footer,
}) {
  /** @type {import('react').MutableRefObject<EventTarget | null>} */
  const mouseDownTarget = useRef(null);
  const hasInput = useRef(false);
  // .modal-backdrop's z-index is a fixed 700 in globals.css — fine for a
  // single modal, but when modals stack (pushModal on top of an already-open
  // modal) every backdrop shares that same value, so which one paints on top
  // depends on DOM order alone. That's fragile enough under real compositor
  // timing that it produced click-interception failures on two different
  // modal pairs in the same run (#6042, #6053) even though DOM order was
  // correct. Deriving z-index from stack depth makes the ordering explicit
  // and no longer dependent on paint-order tie-breaking.
  const stackDepth = useContext(ModalStackDepthContext);
  const resolved = resolveModalSize({ size, wide, maxWidth });

  const handleClose = () => {
    const isDirty = dirty !== undefined ? dirty : hasInput.current;
    if (
      isDirty &&
      !window.confirm(dirtyMessage || 'You have unsaved changes. Are you sure you want to close?')
    ) {
      return;
    }
    onClose();
  };

  return (
    <div
      className="modal-backdrop"
      style={{ zIndex: 700 + stackDepth * 10 }}
      onMouseDown={(e) => {
        mouseDownTarget.current = e.target;
      }}
      onClick={(e) => {
        if (
          closable &&
          closeOnBackdrop &&
          e.target === e.currentTarget &&
          mouseDownTarget.current === e.currentTarget
        ) {
          handleClose();
        }
      }}
    >
      <div
        className="modal-box"
        data-size={resolved.size}
        style={{
          width: '100%',
          maxWidth: resolved.maxWidth,
          ...(fixedHeight ? { height: fixedHeight } : {}),
        }}
        onInput={() => {
          hasInput.current = true;
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {onBack && (
              <button
                onClick={onBack}
                className="btn btn-ghost btn-sm"
                style={{ padding: '4px 8px', minWidth: 0 }}
              >
                ←
              </button>
            )}
            <h2 className="display" style={{ fontSize: 16, fontWeight: 600, color: 'var(--fg)' }}>
              {title}
            </h2>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {headerActions}
            {closable && (
              <button
                onClick={handleClose}
                className="btn btn-ghost btn-sm"
                style={{ padding: '4px 8px', minWidth: 0 }}
              >
                ✕
              </button>
            )}
          </div>
        </div>
        <ModalCloseContext.Provider value={handleClose}>
          <div className="modal-body">{children}</div>
          {footer && <div className="modal-footer">{footer}</div>}
        </ModalCloseContext.Provider>
      </div>
    </div>
  );
}

export default Modal;
